import React, {
  useCallback,
  useRef,
} from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Link } from 'expo-router';

import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';

/** Keys that open the menu from the keyboard, per the Radix trigger contract. */
const OPEN_KEYS = new Set(['Enter', ' ', 'ArrowDown', 'ArrowUp']);

function getRelatedTarget(event) {
  return event?.relatedTarget ?? event?.nativeEvent?.relatedTarget ?? null;
}

/** Ignore leave events that move within the trigger + portaled panel hover zone. */
function shouldScheduleMegaMenuClose(event, menuId) {
  const related = getRelatedTarget(event);
  if (!related || typeof related.closest !== 'function') return true;
  return related.closest(`[data-nav-mega-menu-zone="${menuId}"]`) == null;
}

/**
 * Hover-or-click nav panel on the shared DropdownMenu stack (Radix portals to
 * `document.body`, so the glass header's `overflow: hidden` does not clip it).
 *
 * Panel rows are real `<a href>` via `Link asChild` — same pattern as the footer
 * and the other nav items — so JS crawlers and users who open the menu get
 * followable links, not `router.push` menuitems.
 *
 * Two constraints come out of that `Link asChild` chain and are easy to undo by
 * accident:
 *
 * 1. Never pass `className` to a component that ends up as the `Link` child.
 *    `Link` turns a `className` into a `{ $$css: true }` style entry, and its
 *    Slot then flattens the style array into one object — at which point styleq
 *    reads every remaining style *value* as a class name. Panel rows accept that
 *    trade (they need `.DropdownMenuItem` for hover and theming, so their layout
 *    lives in CSS instead), while the triggers keep real styles and are targeted
 *    through `data-nav-mega-menu-trigger`.
 * 2. Identify elements with `dataSet` on React Native components and with a
 *    plain `data-*` attribute on Radix's DOM nodes — `dataSet` on a real `div`
 *    is not a valid DOM prop.
 *
 * When multiple mega menus sit beside each other, pass coordinated `open` /
 * `onOpen` / `onScheduleClose` / `onClose` from `useNavMegaMenuGroup` so only
 * one panel stays open.
 *
 * @param {object} props
 * @param {string} props.menuId Stable id for the coordinated hover zone (`docs`, `features`, …).
 * @param {string} props.triggerLabel Nav button label.
 * @param {string} props.triggerHref Crawlable hub URL for the trigger.
 * @param {boolean} [props.isActive] Highlight state for the trigger.
 * @param {Array<{ label: string, items: Array<{ href: string, label: string }> }>} props.columns
 * @param {boolean} [props.isLoading]
 * @param {{ href: string, label: string }} [props.footer]
 * @param {object} [props.triggerButtonStyle] Style override for the trigger button.
 * @param {string} [props.triggerButtonSize] Button size for the trigger (`small` | `medium`).
 * @param {string} [props.triggerTextWeight] Label weight for the trigger (`medium` = 500).
 * @param {boolean} props.open Controlled open state.
 * @param {() => void} props.onOpen Open immediately (closes sibling menus via parent).
 * @param {() => void} props.onScheduleClose Hover-out grace period before close.
 * @param {() => void} props.onClose Close immediately.
 */
export default function NavMegaMenu({
  menuId,
  triggerLabel,
  triggerHref,
  isActive = false,
  columns = [],
  isLoading = false,
  footer,
  triggerButtonStyle,
  triggerButtonSize = 'medium',
  triggerTextWeight = 'normal',
  open = false,
  onOpen,
  onScheduleClose,
  onClose,
}) {
  /**
   * Hover-opened menus must not steal focus, but keyboard-opened ones have to —
   * otherwise Radix's roving focus never engages and the panel links are
   * unreachable without a mouse.
   */
  const openedByPointer = useRef(false);

  const openMenu = useCallback(() => {
    onOpen?.();
  }, [onOpen]);

  const openFromPointer = useCallback(() => {
    openedByPointer.current = true;
    onOpen?.();
  }, [onOpen]);

  const scheduleCloseFromEvent = useCallback((event) => {
    if (Platform.OS !== 'web') return;
    if (!shouldScheduleMegaMenuClose(event, menuId)) return;
    onScheduleClose?.();
  }, [menuId, onScheduleClose]);

  const closeMenu = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleTriggerKeyDown = useCallback((event) => {
    if (OPEN_KEYS.has(event?.key)) openedByPointer.current = false;
  }, []);

  const handleOpenChange = useCallback((next) => {
    if (next) openMenu();
    else closeMenu();
  }, [openMenu, closeMenu]);

  const handleAutoFocus = useCallback((event) => {
    if (openedByPointer.current) event.preventDefault();
  }, []);

  const itemCount = columns.reduce((n, col) => n + (col.items?.length || 0), 0);
  const triggerStyle = triggerButtonStyle ?? { minWidth: 60, paddingHorizontal: 12 };

  const webHoverProps = Platform.OS === 'web'
    ? { onMouseEnter: openFromPointer, onMouseLeave: scheduleCloseFromEvent }
    : {};

  return (
    <DropdownMenu.Root open={open} onOpenChange={handleOpenChange} modal={false}>
      <DropdownMenu.Trigger asChild>
        <Link href={triggerHref} asChild>
          <NavigationBarButton
            label={triggerLabel}
            isRound
            size={triggerButtonSize}
            textWeight={triggerTextWeight}
            variant={isActive ? 'navActive' : open ? 'navOpen' : 'nav'}
            appendIcon={open ? 'chevron-up' : 'chevron-down'}
            appendIconSize={10}
            style={triggerStyle}
            dataSet={{ publicNavButton: 'true', navMegaMenuTrigger: 'true', navMegaMenuZone: menuId }}
            onKeyDown={handleTriggerKeyDown}
            {...webHoverProps}
          />
        </Link>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content
        className="DropdownMenuContent NavMegaMenuContent"
        data-nav-mega-menu-zone={menuId}
        sideOffset={10}
        align="start"
        onOpenAutoFocus={handleAutoFocus}
        onCloseAutoFocus={handleAutoFocus}
        {...webHoverProps}
      >
        <View style={styles.body}>
          {isLoading && itemCount === 0 ? (
            <DropdownMenuLabel text="Loading…" />
          ) : null}

          {!isLoading && itemCount === 0 ? (
            <DropdownMenuLabel text="Could not load items" />
          ) : null}

          <View style={styles.grid}>
            {columns.map((column) => (
              <View key={column.label} style={styles.column}>
                <DropdownMenuLabel text={column.label} />
                {column.items.map((item) => (
                  <NavMegaMenuLink
                    key={item.href}
                    href={item.href}
                    label={item.label}
                    menuId={menuId}
                    onNavigate={closeMenu}
                    onKeepOpen={openFromPointer}
                  />
                ))}
              </View>
            ))}
          </View>

          {footer ? (
            <>
              <DropdownMenu.Separator className="DropdownMenuSeparator" />
              <NavMegaMenuLink
                href={footer.href}
                label={footer.label}
                icon="arrow-right"
                menuId={menuId}
                onNavigate={closeMenu}
                onKeepOpen={openFromPointer}
              />
            </>
          ) : null}
        </View>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

/**
 * One crawlable row inside the panel. `DropdownMenu.Item asChild` keeps keyboard
 * and focus behaviour; `Link asChild` makes React Native Web render an `<a>`.
 *
 * Layout is CSS-only (`[data-nav-link-row]`) rather than a style prop — see the
 * `className` note on `NavMegaMenu`.
 */
function NavMegaMenuLink({ href, label, icon, menuId, onNavigate, onKeepOpen }) {
  const webHoverProps = Platform.OS === 'web' ? { onMouseEnter: onKeepOpen } : {};

  return (
    <DropdownMenu.Item asChild className="DropdownMenuItem">
      <Link href={href} asChild>
        <Pressable
          onPress={onNavigate}
          dataSet={{ navLinkRow: 'true', navMegaMenuZone: menuId }}
          {...webHoverProps}
        >
          {icon ? (
            <Icon name={icon} size={14} color="primary" />
          ) : null}
          <Text size="n" color="primary" numberOfLines={1}>
            {label}
          </Text>
        </Pressable>
      </Link>
    </DropdownMenu.Item>
  );
}

const styles = StyleSheet.create({
  body: {
    width: '100%',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    columnGap: 20,
    rowGap: 4,
  },
  column: {
    minWidth: 176,
    flexGrow: 1,
    flexBasis: 176,
  },
});

export { NavMegaMenuLink };
